import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ObjectId } from 'bson';
import { useFinancingSubmitRepaymentReceiptApi } from '@hero/api-hooks';
import {
  Button,
  Checkbox,
  DateField,
  FileUploader,
  Label,
  LenderChip,
  Price,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
  type LenderDisplay,
} from '@hero/web-components';
import { fileToBase64 } from '@hero/web-utils';
import { AlertCircleIcon } from '@hero/web-icons';
import type { ApplicationMock, OfferMock } from '../mocks';
import { formatOfferRepaymentDate, formatOfferTerm } from '../utils/offerDisplay';

export type MarkAsPaidDrawerProps = {
  /**
   * Controls drawer visibility. We keep the API symmetrical with
   * the other Financing drawers (`FinancingProfileDrawer`,
   * `EditFinancingProfileDialog` → drawer) so the host page just
   * toggles a boolean.
   */
  isOpen: boolean;
  application: ApplicationMock;
  /**
   * The accepted offer on this application — used to seed the
   * "expected repayment" summary at the top of the drawer. Carrier
   * sees exactly what they're claiming to have paid.
   */
  acceptedOffer: OfferMock | undefined;
  /** Resolved lender display (logo, name, brand color), if known. */
  partner?: LenderDisplay;
  onClose: () => void;
};

/**
 * Carrier-side "Mark as paid" drawer — the canonical surface for
 * uploading proof that they wired the repayment to the lender.
 *
 * Drawer (Sheet) rather than a centered dialog: the form carries a
 * date input, file uploader, multi-line notes, an amber anti-fraud
 * notice, and a long acknowledgement checkbox — too much vertical
 * content for a centered modal. The drawer pattern matches
 * {@link FinancingProfileDrawer} on this same page so the carrier
 * sees the same "slide-in panel" affordance across the financing
 * surface.
 *
 * Mirrors the lender-side `LenderRepaymentPanel` shape so both
 * sides of the wall feel like the same operation viewed from
 * different angles.
 *
 *  - **Repaid on** — carrier-declared date the wire cleared.
 *    Stored under `repaymentReceipt.repaidAt` and surfaced to the
 *    reviewer next to the server-stamped `submittedAt` so back-
 *    dating is auditable.
 *  - **Transaction proof** — required (PDF / JPG / PNG). Hint
 *    enumerates the realistic upload set (transfer receipt,
 *    bank-statement screenshot, SWIFT confirmation, etc.).
 *  - **Notes** — optional context for the reviewer.
 *  - **Acknowledgement** — required. Stronger language than the
 *    accept-offer dialog: explicitly calls out that false /
 *    misleading information can result in suspension, and that the
 *    receipt will be reviewed. The amber callout above the
 *    checkbox makes the wording impossible to skim past.
 *
 * Backend gating: the submit endpoint accepts the upload only when
 * `application.status === 'funded'` (or already
 * `'awaiting_repayment_approval'`, in which case the previous
 * upload is replaced). The success transition is to
 * `'awaiting_repayment_approval'`; the application only flips to
 * `'repaid'` once an admin or the lender approves the receipt.
 */
export function MarkAsPaidDrawer({
  isOpen,
  application,
  acceptedOffer,
  partner,
  onClose,
}: MarkAsPaidDrawerProps) {
  const { t } = useTranslation('m-financing');
  const submit = useFinancingSubmitRepaymentReceiptApi();

  const [repaidOn, setRepaidOn] = useState<Date | undefined>(() => new Date());
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  /**
   * Reset transient form state every time the drawer is opened so
   * a previous abandoned attempt doesn't haunt the next one. We do
   * this on the opening edge of `isOpen` rather than in
   * `onClose` because the host could reopen the drawer with a
   * different application (rare on this surface, but the reset
   * keeps the contract robust).
   */
  useEffect(() => {
    if (isOpen) {
      setRepaidOn(new Date());
      setProofFile(null);
      setNotes('');
      setAcknowledged(false);
    }
  }, [isOpen]);

  if (!acceptedOffer || !acceptedOffer.terms) return null;

  const terms = acceptedOffer.terms;
  const currency = application.invoice.currency;
  const repaymentTotal = terms.advancedAmount + terms.feeAmount;

  const canSubmit =
    Boolean(repaidOn) &&
    Boolean(proofFile) &&
    acknowledged &&
    !submit.isPending;

  const handleSubmit = async () => {
    if (!repaidOn || !proofFile) return;
    try {
      const upload = await fileToBase64(proofFile);
      await submit.mutateAsync({
        _id: new ObjectId(application.id),
        image: { upload },
        notes: notes.trim() || undefined,
        at: repaidOn,
      });
      toast.success(t('mark_as_paid.toasts.success'));
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('mark_as_paid.toasts.failed'),
      );
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <SheetTitle>{t('mark_as_paid.modal_title')}</SheetTitle>
          <SheetDescription>
            {t('mark_as_paid.modal_description')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 p-6">
          <div className="flex items-center justify-between gap-3">
            {partner ? (
              <LenderChip lender={partner} size="sm" />
            ) : (
              <span className="text-sm font-semibold text-gray-900">
                {acceptedOffer.lenderName}
              </span>
            )}
          </div>

          {/* Headline: total being repaid */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-xs font-medium text-emerald-800">
              {t('mark_as_paid.summary.total_to_repay')}
            </p>
            <Price
              amount={repaymentTotal}
              currency={currency}
              size="lg"
              className="mt-1 font-bold text-emerald-700"
            />
            <p className="mt-2 text-xs leading-relaxed text-emerald-800/80">
              {t('mark_as_paid.summary.hint')}
            </p>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-gray-200 bg-white p-3 text-sm">
            <div>
              <dt className="text-xs text-gray-500">
                {t('mark_as_paid.summary.advance')}
              </dt>
              <dd className="mt-0.5">
                <Price amount={terms.advancedAmount} currency={currency} />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('mark_as_paid.summary.fee')}
              </dt>
              <dd className="mt-0.5">
                <Price amount={terms.feeAmount} currency={currency} />
                <span className="ms-1 text-xs text-gray-500">
                  ({(terms.feePercentage * 100).toFixed(1)}%)
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('mark_as_paid.summary.term')}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">
                {formatOfferTerm(t, acceptedOffer)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('mark_as_paid.summary.repayment_date')}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">
                {formatOfferRepaymentDate(terms.repaymentDate)}
              </dd>
            </div>
          </dl>

          {/**
           * Operational inputs the carrier owns. We stack them
           * vertically (one field per row) on the narrower drawer
           * so each input gets the full width — the proof uploader
           * in particular reads better as a full-bleed dropzone
           * rather than a half-column thumbnail.
           */}
          <div className="space-y-4">
            <DateField
              label={t('mark_as_paid.fields.repaid_on.title')}
              required
              value={repaidOn}
              onChange={setRepaidOn}
            />
            <div className="flex flex-col gap-1.5">
              <Label className="flex items-center gap-1">
                {t('mark_as_paid.fields.proof.title')}
                <span className="text-red-600" aria-hidden="true">
                  *
                </span>
              </Label>
              <FileUploader
                files={proofFile ? [proofFile] : []}
                onChange={files => setProofFile(files[0] ?? null)}
                label={t('mark_as_paid.fields.proof.label')}
                variant="compact"
                acceptedFileTypes={{
                  'application/pdf': [],
                  'image/png': [],
                  'image/jpeg': [],
                }}
                hint={t('mark_as_paid.fields.proof.hint')}
              />
              {!proofFile && (
                <p className="text-xs text-amber-700">
                  {t('mark_as_paid.fields.proof.required_hint')}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('mark_as_paid.fields.notes.title')}</Label>
              <Textarea
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={t('mark_as_paid.fields.notes.placeholder')}
              />
            </div>
          </div>

          {/**
           * Anti-fraud notice + acknowledgement. We render the
           * warning as an explicit amber callout, separate from
           * the checkbox row, so the carrier can't visually skim
           * past it. The checkbox label uses the same enforcement
           * wording the support team can later quote back if a
           * carrier disputes a suspension.
           */}
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
            <div className="flex items-start gap-2">
              <AlertCircleIcon className="mt-0.5 size-4 shrink-0 text-amber-700" />
              <div className="space-y-1">
                <p className="text-xs font-semibold text-amber-900">
                  {t('mark_as_paid.warning.title')}
                </p>
                <p className="text-xs leading-relaxed text-amber-800">
                  {t('mark_as_paid.warning.description')}
                </p>
              </div>
            </div>
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-white p-3">
            <Checkbox
              className="mt-0.5"
              checked={acknowledged}
              onCheckedChange={value => setAcknowledged(value === true)}
              disabled={submit.isPending}
            />
            <span className="text-sm leading-relaxed text-gray-700">
              {t('mark_as_paid.fields.acknowledge.label')}
            </span>
          </label>
        </div>

        <SheetFooter className="sticky bottom-0 border-t bg-white px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submit.isPending}>
            {t('mark_as_paid.actions.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="gap-2">
            {submit.isPending ? (
              t('mark_as_paid.actions.submitting')
            ) : (
              <>
                {t('mark_as_paid.actions.confirm')}
                <Price
                  amount={repaymentTotal}
                  currency={currency}
                  size="sm"
                  className="font-semibold"
                />
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
